import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CreditCard, FileText } from "lucide-react";
import MarkingPatternModal from "@/components/MarkingPatternModal";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { STEPS, MARKS, DEVANAGARI } from "@/mock/data";
import GovHeader from "@/components/GovHeader";
import Stepper from "@/components/Stepper";
import ActionBar from "@/components/ActionBar";
import { Step1, Step2, Step3 } from "@/components/steps/FormSteps1";
import { Step4, Step5, Step6 } from "@/components/steps/FormSteps2";
import { Step7, Step8, Step9 } from "@/components/steps/FormSteps3";
import { Step10, Step11 } from "@/components/steps/FormSteps4";
import PreviewStep from "@/components/steps/PreviewStep";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const CARD_TITLES = {
  1: "अर्जदाराची वैयक्तिक माहिती",
  2: "अर्जदाराची शैक्षणिक माहिती",
  3: "संगणक व डिजिटल साक्षरता",
  4: "आपले सरकार सेवा केंद्राच्या अनुषंगाने उपलब्ध पायाभूत सुविधांची माहिती",
  5: "आर्थिक क्षमता माहिती",
  6: "अनुभव — CSC / महा-ई सेवा केंद्र / बँकिंग प्रतिनिधी / ई-गव्हर्नन्स",
  7: "व्यवसाय आराखडा व शाश्वतता माहिती",
  8: "सामाजिक समावेशकता",
  9: "चारित्र्य पडताळणी",
  10: "अर्ज शुल्क",
  11: "नवीन आपले सरकार सेवा केंद्र अर्जासोबत सादर करावयाची आवश्यक कागदपत्रे",
  12: "अर्जाचे पूर्वावलोकन",
};

const PATTERNS = {
  pincode: { re: /^\d{6}$/, msg: "कृपया वैध ६ अंकी पिनकोड प्रविष्ट करा." },
  mobile: { re: /^[6-9]\d{9}$/, msg: "कृपया वैध १० अंकी मोबाईल क्रमांक प्रविष्ट करा." },
  aadhaar: { re: /^\d{12}$/, msg: "कृपया वैध १२ अंकी आधार क्रमांक प्रविष्ट करा." },
  pan: { re: /^[A-Z]{5}[0-9]{4}[A-Z]$/, msg: "कृपया वैध PAN क्रमांक प्रविष्ट करा. (उदा. ABCDE1234F)" },
  email: { re: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, msg: "कृपया वैध ई-मेल आयडी प्रविष्ट करा." },
  latlng: { re: /^-?\d{1,3}\.\d{6,}$/, msg: "किमान ६ दशांश स्थळांसह वैध मूल्य प्रविष्ट करा (उदा. 16.852345)" },
};

function req(data, errors, name, msg = "हे क्षेत्र आवश्यक आहे.") {
  const v = data[name];
  const empty = v === undefined || v === "" || v === "-- निवडा --" || v === null;
  if (empty) errors[name] = msg;
}
function pat(data, errors, name, key) {
  const v = data[name];
  if (v && !PATTERNS[key].re.test(String(v).trim())) errors[name] = PATTERNS[key].msg;
}

function validate(step, data) {
  const e = {};
  switch (step) {
    case 1:
      ["orgType", "taluka", "village", "circle", "photo", "nameMrFirst", "nameMrLast", "nameEnFirst", "nameEnLast", "dob", "gender", "mobile", "email", "aadhaar", "pan", "aadhaar_file", "pan_file", "address", "city", "tehsil", "pincode", "residence_proof_file", "residence_proof_file_2", "self_declaration_file"].forEach((n) => req(data, e, n));
      if (["नगरपंचायत", "नगरपरिषद", "महानगरपालिका"].includes(data.orgType)) req(data, e, "lbWard", "वॉर्ड / प्रभाग क्रमांक आवश्यक आहे.");
      pat(data, e, "pincode", "pincode");
      pat(data, e, "mobile", "mobile");
      pat(data, e, "aadhaar", "aadhaar");
      pat(data, e, "pan", "pan");
      pat(data, e, "email", "email");
      if (data.dpdpConsent !== true) e.dpdpConsent = "पुढे जाण्यासाठी गोपनीयता संमती आवश्यक आहे.";
      break;
    case 2: {
      // HSC (row 0) पास असणे आवश्यक
      req(data, e, "edu_passed_0", "किमान १२ वी (HSC) उत्तीर्ण असणे आवश्यक — 'होय' निवडा.");
      // ज्या ओळीत 'होय' निवडले आहे त्या ओळीतील सर्व माहिती व प्रमाणपत्र आवश्यक
      for (let i = 0; i < 4; i++) {
        if (data[`edu_passed_${i}`] === "yes") {
          req(data, e, `edu_board_${i}`, "आवश्यक");
          req(data, e, `edu_year_${i}`, "आवश्यक");
          req(data, e, `edu_pct_${i}`, "आवश्यक");
          req(data, e, `edu_file_${i}`, "प्रमाणपत्र अपलोड करा");
        }
      }
      if (data.edu_passed_2 === "yes" && data.edu_passed_1 !== "yes") {
        e.edu_passed_1 = "पदव्युत्तर पदवी भरल्यास पदवी उत्तीर्ण असणे आवश्यक आहे — पदवीसाठी 'होय' निवडा.";
      }
      break;
    }
    case 3: {
      // MSCIT (row 0) उत्तीर्ण असणे आवश्यक
      req(data, e, "cl_passed_0", "MSCIT किंवा समकक्ष प्रमाणपत्र आवश्यक — 'होय' निवडा.");
      for (let i = 0; i < 3; i++) {
        if (data[`cl_passed_${i}`] === "yes") {
          req(data, e, `cl_name_${i}`, "आवश्यक");
          req(data, e, `cl_year_${i}`, "आवश्यक");
          req(data, e, `cl_pct_${i}`, "आवश्यक");
          req(data, e, `cl_file_${i}`, "प्रमाणपत्र अपलोड करा");
        }
      }
      break;
    }
    case 4:
      req(data, e, "office_address", "प्रस्तावित कार्यालयाच्या जागेचा पत्ता आवश्यक");
      req(data, e, "office_lat", "अक्षांश आवश्यक");
      req(data, e, "office_lng", "रेखांश आवश्यक");
      pat(data, e, "office_lat", "latlng");
      pat(data, e, "office_lng", "latlng");
      for (let i = 0; i < 14; i++) {
        req(data, e, `infra_${i}`, "आहे/नाही निवडा.");
        if (data[`infra_${i}`] === "yes") {
          req(data, e, `infra_qty_${i}`, "तपशील आवश्यक");
          if (i === 2) req(data, e, `infra_file_${i}`, "पुरावा अपलोड करा");
        }
      }
      break;
    case 5:
      for (let i = 0; i < 4; i++) {
        req(data, e, `fin_plan_${i}`, "आहे/नाही निवडा.");
        if (data[`fin_plan_${i}`] === "yes") {
          req(data, e, `fin_detail_${i}`, "तपशील आवश्यक");
          req(data, e, `fin_file_${i}`, "पुरावा अपलोड करा");
        }
      }
      break;
    case 6:
      ["cscExp", "bankRep", "egovExp", "prevCenter"].forEach((n) => req(data, e, n, "आहे/नाही निवडा."));
      if (data.cscExp === "yes") {
        ["csc_id", "csc_addr", "csc_taluka", "csc_pin", "csc_file"].forEach((n) => req(data, e, n, "आवश्यक"));
      }
      if (data.bankRep === "yes") {
        ["bk_id", "bk_addr", "bk_taluka", "bk_pin", "bk_file"].forEach((n) => req(data, e, n, "आवश्यक"));
      }
      if (data.egovExp === "yes") {
        ["eg_details", "eg_file"].forEach((n) => req(data, e, n, "आवश्यक"));
      }
      if (data.prevCenter === "yes") {
        ["pc_addr", "pc_id", "pc_file"].forEach((n) => req(data, e, n, "आवश्यक"));
      }
      break;
    case 7:
      for (let i = 0; i < 4; i++) {
        req(data, e, `bp_plan_${i}`, "आहे/नाही निवडा.");
        if (data[`bp_plan_${i}`] === "yes") {
          const wc = (data[`bp_text_${i}`] || "").trim().split(/\s+/).filter(Boolean).length;
          if (wc < 25) e[`bp_text_${i}`] = "किमान २५ शब्द लिहा.";
        }
      }
      break;
    case 8:
      ["si_disabled", "si_exservice", "si_shg", "si_entrepreneur"].forEach((n) => {
        req(data, e, n, "आहे/नाही निवडा.");
        if (data[n] === "yes") req(data, e, `${n}_file`, "पुरावा अपलोड करा");
      });
      break;
    case 9:
      ["cv_station", "cv_number", "cv_date", "cv_file", "cv_fraud", "cv_blacklist"].forEach((n) => req(data, e, n));
      if (data.cv_fraud === "no" || data.cv_blacklist === "no") {
        req(data, e, "cv_affidavit_file", "प्रतिज्ञापत्र अपलोड करा");
      }
      break;
    case 10:
      req(data, e, "fee_utr");
      req(data, e, "fee_method");
      req(data, e, "fee_proof");
      break;
    case 11:
      // Step 11 is now a review/preview of uploaded documents; no extra required fields.
      break;
    case 12:
      if (!data.declaration) e.declaration = "कृपया घोषणा स्वीकारा.";
      req(data, e, "sign_name");
      req(data, e, "sign_mobile");
      pat(data, e, "sign_mobile", "mobile");
      req(data, e, "decl_place", "ठिकाण भरा");
      break;
    default:
      break;
  }
  return e;
}

export default function ApplicationForm() {
  const { application, goToStep, submitApplication, requestSubmitOtp, saveDraft, setFields } = useApp();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [draftSaved, setDraftSaved] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [otpPhase, setOtpPhase] = useState(false);
  const [submitOtp, setSubmitOtp] = useState("");
  const [otpInfo, setOtpInfo] = useState("");
  const [otpError, setOtpError] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);
  const [markConfirmed, setMarkConfirmed] = useState(false);

  React.useEffect(() => {
    if (application && application.status !== "DRAFT") {
      navigate("/success", { replace: true });
    }
  }, [application, navigate]);

  // Show the marking-pattern popup once per login session.
  React.useEffect(() => {
    if (sessionStorage.getItem("assk_marking_seen")) return;
    setMarkOpen(true);
    sessionStorage.setItem("assk_marking_seen", "1");
  }, []);

  if (!application) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-[15px] text-slate-500">
        अर्ज लोड होत आहे...
      </div>
    );
  }

  const step = application.currentStep;

  const renderStep = () => {
    switch (step) {
      case 1: return <Step1 errors={errors} />;
      case 2: return <Step2 errors={errors} />;
      case 3: return <Step3 errors={errors} />;
      case 4: return <Step4 errors={errors} />;
      case 5: return <Step5 errors={errors} />;
      case 6: return <Step6 errors={errors} />;
      case 7: return <Step7 errors={errors} />;
      case 8: return <Step8 errors={errors} />;
      case 9: return <Step9 errors={errors} />;
      case 10: return <Step10 errors={errors} />;
      case 11: return <Step11 />;
      case 12: return <PreviewStep errors={errors} />;
      default: return null;
    }
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector(".border-\\[\\#dc2626\\]");
      el && el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  };

  const findFirstIncomplete = (maxStep, data) => {
    for (let s = 1; s <= maxStep; s++) {
      if (Object.keys(validate(s, data)).length > 0) return s;
    }
    return 0;
  };

  const handleNext = () => {
    // Steps 1–8: allow moving forward even if some fields are still incomplete.
    if (step <= 8) {
      setErrors({});
      goToStep(step + 1);
      return;
    }

    // Step 9 → 10 gate: every mandatory field in steps 1–9 must be complete.
    if (step === 9) {
      const bad = findFirstIncomplete(9, application.data);
      if (bad) {
        setErrors(validate(bad, application.data));
        toast.error(`टप्पा ${bad} मधील आवश्यक माहिती अपूर्ण आहे. पुढे जाण्यापूर्वी ती पूर्ण करा.`);
        if (bad !== step) goToStep(bad);
        scrollToFirstError();
        return;
      }
      setErrors({});
      goToStep(10);
      return;
    }

    // Step 11: review page, no required fields.
    if (step === 11) {
      setErrors({});
      goToStep(12);
      return;
    }

    // Step 10 → 11: validate application-fee fields.
    if (step === 10) {
      const e = validate(10, application.data);
      setErrors(e);
      if (Object.keys(e).length > 0) {
        toast.error("कृपया आवश्यक माहिती भरा / निवडा.");
        scrollToFirstError();
        return;
      }
      goToStep(11);
      return;
    }

    // Step 12: final submit — full completeness check across steps 1–10 + declaration.
    const t = new Date();
    const today = `${String(t.getDate()).padStart(2, "0")}/${String(t.getMonth() + 1).padStart(2, "0")}/${t.getFullYear()}`;
    const nameMrEff = application.data.nameMr || [application.data.nameMrFirst, application.data.nameMrMiddle, application.data.nameMrLast].filter((p) => p && String(p).trim()).join(" ").trim();
    const backfill = {
      sign_name: nameMrEff || application.data.sign_name || "",
      sign_mobile: application.data.mobile || application.data.sign_mobile || "",
      decl_date: application.data.decl_date || today,
    };
    setFields(backfill);
    const dataForValidation = { ...application.data, ...backfill };
    const bad = findFirstIncomplete(10, dataForValidation);
    if (bad) {
      setErrors(validate(bad, dataForValidation));
      toast.error(`टप्पा ${bad} मधील आवश्यक माहिती अपूर्ण आहे. अर्ज सादर करण्यापूर्वी ती पूर्ण करा.`);
      goToStep(bad);
      scrollToFirstError();
      return;
    }
    const e = validate(12, dataForValidation);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("कृपया आवश्यक माहिती भरा / निवडा.");
      scrollToFirstError();
      return;
    }
    setOtpPhase(false);
    setSubmitOtp("");
    setOtpInfo("");
    setOtpError("");
    setMarkConfirmed(false);
    setModalOpen(true);
  };

  const handleBack = () => step > 1 && goToStep(step - 1);

  const handleDraft = async () => {
    try {
      await saveDraft();
      setDraftSaved(true);
      toast.success("प्रारूप सर्व्हरवर जतन झाले ✓");
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (e) {
      toast.error("प्रारूप जतन करताना अडचण आली.");
    }
  };

  const sendSubmitOtp = async () => {
    if (sendingOtp) return;
    setSendingOtp(true);
    setOtpError("");
    try {
      const res = await requestSubmitOtp();
      setOtpInfo(res.message || "OTP तुमच्या ई-मेलवर पाठवला आहे.");
      setOtpPhase(true);
      setSubmitOtp("");
    } catch (e) {
      setOtpError(e.message || "OTP पाठवताना अडचण आली.");
    } finally {
      setSendingOtp(false);
    }
  };

  const doSubmit = async () => {
    if (submitting) return;
    if (!/^\d{6}$/.test(submitOtp)) {
      setOtpError("कृपया ६ अंकी OTP प्रविष्ट करा.");
      return;
    }
    setSubmitting(true);
    setOtpError("");
    try {
      await submitApplication(submitOtp);
      setModalOpen(false);
      toast.success("अर्ज यशस्वीरित्या सादर झाला ✓ पावती तुमच्या ई-मेलवर पाठवली आहे.");
      setTimeout(() => navigate("/success"), 300);
    } catch (e) {
      setOtpError(e.message || "अर्ज सादर करताना अडचण आली.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f2e6] pb-4">
      <div className="mx-auto w-full max-w-6xl">
      <GovHeader />

      {/* Warning banner */}
      <div className="mx-3 mb-3 mt-3 flex items-start gap-2.5 rounded-xl border border-[#E0A106]/50 bg-[#fdf7e5] px-4 py-3 text-[14px] text-[#8a5a06]">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#E0A106]" />
        <span><strong>सूचना:</strong> सर्व अपलोड फाईल्सचा कमाल आकार 1MB आहे. 1MB पेक्षा मोठी फाईल अपलोड केली जाणार नाही.</span>
      </div>

      {/* Marking-pattern reminder banner (steps 1–11) */}
      {step <= 11 && (
        <div className="mx-3 mb-3 flex flex-col gap-2 rounded-xl border border-[#1b5e3f]/25 bg-[#eef6ef] px-4 py-3 text-[14px] text-[#1b5e3f] sm:flex-row sm:items-center">
          <FileText className="hidden h-5 w-5 shrink-0 text-[#1b5e3f] sm:block" />
          <span className="flex-1">
            <strong>महत्त्वाचे:</strong> अर्ज गुणांकनानुसार तपासला जातो. कृपया प्रत्येक टप्पा भरताना <strong>निवड प्रक्रिया — गुण विभागणी</strong> तक्ता पाहा.
          </span>
          <button
            onClick={() => setMarkOpen(true)}
            data-testid="marking-view-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#1b5e3f] px-4 py-2 text-[13.5px] font-semibold text-white hover:opacity-90"
          >
            <FileText className="h-4 w-4" /> गुण विभागणी पहा
          </button>
        </div>
      )}

      {/* App number badge */}
      <div className="px-3">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#1b5e3f]/25 bg-white px-4 py-2 text-[14px] font-semibold text-[#1b5e3f] shadow-sm">
          <span className="h-2 w-2 rounded-full bg-[#5a9e3a]" />
          अर्ज शीर्षक: <span className="font-latin font-bold">{application.applicationNumber}</span>
        </span>
      </div>

      <div className="mt-3">
        <Stepper />
      </div>

      {/* Form card */}
      <div className="mx-3 mb-3 overflow-hidden rounded-2xl border border-[#e6dcc4] bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b-2 border-[#f1ead7] bg-[#fbf9f2] px-5 py-4">
          <span className="flex h-10 w-10 min-w-[2.5rem] items-center justify-center rounded-xl bg-[#1b5e3f] text-[15px] font-bold text-white shadow-sm">
            {DEVANAGARI[step]}
          </span>
          <h2 className="font-head flex-1 text-[19px] font-medium text-[#1b5e3f] sm:text-[21px]">{CARD_TITLES[step]}</h2>
          {MARKS[step] && (
            <span className="rounded-full bg-[#fdf7e5] px-3.5 py-1 text-[13px] font-bold text-[#b8790a] ring-1 ring-[#E0A106]/30">{MARKS[step]}</span>
          )}
        </div>
        <div className="p-5">{renderStep()}</div>
      </div>

      {step === 10 && (
        <div className="mx-3 mb-3">
          <PaymentHint />
        </div>
      )}

      <ActionBar
        onBack={handleBack}
        onDraft={handleDraft}
        onNext={handleNext}
        isPreview={step === 12}
        backDisabled={step === 1}
        nextLabel={step === 11 ? "पूर्वावलोकन पहा" : "पुढे जा"}
        draftSaved={draftSaved}
      />
      </div>

      <footer className="border-t-2 border-[#E0A106]/30 bg-[#fbf9f2] py-4 text-center text-[13px] text-[#7a6a45]">
        Copyright © 2026 Collector Office, Amravati
      </footer>

      {/* Confirm submit modal (shadcn Dialog) */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md text-center">
          {!otpPhase ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-center text-[19px] text-[#1b5e3f]">आपण अर्ज सादर करू इच्छिता का?</DialogTitle>
              </DialogHeader>
              <p className="text-[14px] text-slate-500">सादर करण्यापूर्वी तुमच्या नोंदणीकृत ई-मेलवर पडताळणीसाठी OTP पाठवला जाईल. सादर केल्यानंतर अर्जात बदल करता येणार नाही.</p>
              <label className="mt-1 flex items-start gap-2.5 rounded-lg border border-[#1b5e3f]/25 bg-[#eef6ef] px-3.5 py-3 text-left text-[13.5px] leading-relaxed text-[#1b5e3f]">
                <input
                  type="checkbox"
                  checked={markConfirmed}
                  onChange={(e) => setMarkConfirmed(e.target.checked)}
                  data-testid="marking-confirm-checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#1b5e3f]"
                />
                <span>
                  मी <button type="button" onClick={() => setMarkOpen(true)} data-testid="marking-open-link" className="font-semibold text-[#1b5e3f] underline">निवड प्रक्रिया — गुण विभागणी</button> तक्ता वाचला असून त्यानुसार माझा अर्ज पूर्ण भरला आहे.
                </span>
              </label>
              {otpError && <p className="text-[13px] text-red-600" data-testid="submit-otp-error">{otpError}</p>}
              <DialogFooter className="mt-2 flex-row justify-center gap-3 sm:justify-center">
                <button onClick={() => setModalOpen(false)} className="rounded-lg border border-slate-400 bg-white px-5 py-2 text-[14px] font-semibold text-slate-700 hover:bg-slate-50" data-testid="submit-cancel-btn">
                  रद्द करा
                </button>
                <button onClick={sendSubmitOtp} disabled={sendingOtp || !markConfirmed} className="rounded-lg bg-[#5a9e3a] px-5 py-2 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-60" data-testid="send-submit-otp-btn">
                  {sendingOtp ? "OTP पाठवत आहे..." : "OTP मिळवा"}
                </button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-center text-[19px] text-[#1b5e3f]">ई-मेल OTP पडताळणी</DialogTitle>
              </DialogHeader>
              {otpInfo && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700" data-testid="submit-otp-info">{otpInfo}</p>}
              <input
                value={submitOtp}
                onChange={(e) => setSubmitOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoFocus
                placeholder="______"
                data-testid="submit-otp-input"
                className="mx-auto w-48 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center text-[22px] font-bold tracking-[0.5em] outline-none focus:border-[#1b5e3f] focus:ring-2 focus:ring-[#1b5e3f]/20"
              />
              <p className="text-[12px] text-slate-400">OTP ५ मिनिटांसाठी वैध आहे.</p>
              {otpError && <p className="text-[13px] text-red-600" data-testid="submit-otp-error">{otpError}</p>}
              <DialogFooter className="mt-2 flex-row justify-center gap-3 sm:justify-center">
                <button onClick={sendSubmitOtp} disabled={sendingOtp} className="rounded-lg border border-slate-400 bg-white px-5 py-2 text-[14px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60" data-testid="resend-submit-otp-btn">
                  {sendingOtp ? "..." : "पुन्हा पाठवा"}
                </button>
                <button onClick={doSubmit} disabled={submitting} className="rounded-lg bg-[#5a9e3a] px-5 py-2 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-60" data-testid="confirm-submit-btn">
                  {submitting ? "सादर करत आहे..." : "अर्ज सादर करा"}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <MarkingPatternModal open={markOpen} onOpenChange={setMarkOpen} />
    </div>
  );
}

function PaymentHint() {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-2.5 text-[13.5px] text-slate-600">
      <CreditCard className="h-4 w-4 text-[#1b5e3f]" />
      वरील बँक तपशीलावर अर्ज शुल्क भरून UTR क्रमांक व पेमेंट पुरावा येथे अपलोड करा.
    </div>
  );
}
